import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
  type RefObject,
} from 'react';

const TOUCH_MOVE_THRESHOLD_PX = 10;

type TouchTapToggleTriggerProps = {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: (event: PointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: PointerEvent<HTMLElement>) => void;
};

type TouchTapToggleOptions = {
  /** Close when the surrounding IonContent scrolls while open. Default true. */
  closeOnScroll?: boolean;
};

type TouchGestureState = {
  pointerId: number | null;
  startX: number;
  startY: number;
  moved: boolean;
};

const INITIAL_GESTURE: TouchGestureState = {
  pointerId: null,
  startX: 0,
  startY: 0,
  moved: false,
};

/**
 * Controlled open state for Radix menus on touch devices.
 *
 * Radix DropdownMenu opens on pointerdown, which fires when a scroll gesture
 * starts on the trigger. This hook blocks that for touch pointers, toggles on
 * pointerup when movement stays within a small threshold, and closes on scroll.
 */
export function useTouchTapToggle<T extends HTMLElement = HTMLButtonElement>(
  options: TouchTapToggleOptions = {},
) {
  const { closeOnScroll = true } = options;
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<T | null>(null);
  const gestureRef = useRef<TouchGestureState>(INITIAL_GESTURE);

  const resetGesture = useCallback(() => {
    gestureRef.current = INITIAL_GESTURE;
  }, []);

  const markMovedIfNeeded = useCallback((clientX: number, clientY: number) => {
    const gesture = gestureRef.current;
    if (gesture.pointerId === null || gesture.moved) return;

    const dx = Math.abs(clientX - gesture.startX);
    const dy = Math.abs(clientY - gesture.startY);
    if (dx > TOUCH_MOVE_THRESHOLD_PX || dy > TOUCH_MOVE_THRESHOLD_PX) {
      gesture.moved = true;
      setOpen(false);
    }
  }, []);

  const onOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
  }, []);

  const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    if (event.pointerType !== 'touch') return;

    // Radix runs after this handler and skips its toggle when defaultPrevented.
    event.preventDefault();

    gestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };

    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (event.pointerType !== 'touch') return;
      if (gestureRef.current.pointerId !== event.pointerId) return;
      markMovedIfNeeded(event.clientX, event.clientY);
    },
    [markMovedIfNeeded],
  );

  const onPointerUp = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (event.pointerType !== 'touch') return;
      if (gestureRef.current.pointerId !== event.pointerId) return;

      const { moved } = gestureRef.current;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      resetGesture();

      if (!moved) {
        setOpen((previousOpen) => !previousOpen);
      }
    },
    [resetGesture],
  );

  const onPointerCancel = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (gestureRef.current.pointerId !== event.pointerId) return;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      resetGesture();
    },
    [resetGesture],
  );

  useEffect(() => {
    if (!closeOnScroll || !open) return;

    const trigger = triggerRef.current;
    const ionContent = trigger?.closest('ion-content');
    if (!ionContent) return;

    const handleScroll = () => {
      setOpen(false);
    };

    ionContent.addEventListener('ionScroll', handleScroll);
    return () => {
      ionContent.removeEventListener('ionScroll', handleScroll);
    };
  }, [closeOnScroll, open]);

  const triggerProps: TouchTapToggleTriggerProps = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  };

  return {
    open,
    onOpenChange,
    triggerRef: triggerRef as RefObject<T>,
    triggerProps,
  };
}
